import { cs } from '../../lib/currency';
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';
import { Percent, DollarSign, Loader2, RotateCcw, TicketPercent } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

/**
 * Reusable Manual Discount dialog used across every billable admin surface:
 * lane bookings, tournament registrations, class enrollments, small-group
 * invoices.
 *
 * Props:
 *   open, onOpenChange    — dialog control
 *   transactionType       — "lane_booking" | "tournament_registration"
 *                           | "class_enrollment" | "small_group_invoice"
 *   transactionId         — id of the target transaction
 *   onChange              — optional: fired after apply/revert so parent can refetch
 */
export default function ManualDiscountDialog({
  open,
  onOpenChange,
  transactionType,
  transactionId,
  onChange,
}) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState(null); // server response from GET
  const [discountType, setDiscountType] = useState('percentage');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');

  const fetchSummary = async () => {
    if (!transactionType || !transactionId) return;
    setLoading(true);
    try {
      const res = await api.get('/admin/manual-discounts', {
        params: { transaction_type: transactionType, transaction_id: transactionId },
      });
      setSummary(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load discount info');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setValue('');
      setReason('');
      setDiscountType('percentage');
      fetchSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, transactionType, transactionId]);

  const parsedValue = Number(value);
  const previewSavings = useMemo(() => {
    if (!summary || !parsedValue || parsedValue <= 0) return 0;
    const original = Number(summary.original_amount || 0);
    if (discountType === 'percentage') {
      if (parsedValue > 100) return 0;
      return Math.round(original * (parsedValue / 100) * 100) / 100;
    }
    return Math.round(Math.min(parsedValue, original) * 100) / 100;
  }, [summary, parsedValue, discountType]);

  const newAmount = useMemo(() => {
    if (!summary) return 0;
    return Math.max(0, Number(summary.original_amount || 0) - previewSavings);
  }, [summary, previewSavings]);

  const submit = async () => {
    if (!reason.trim()) {
      toast.error('Please enter a reason for this discount');
      return;
    }
    if (!parsedValue || parsedValue <= 0) {
      toast.error('Discount value must be greater than zero');
      return;
    }
    if (discountType === 'percentage' && parsedValue > 100) {
      toast.error('Percentage cannot exceed 100');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/admin/manual-discounts', {
        transaction_type: transactionType,
        transaction_id: transactionId,
        discount_type: discountType,
        value: parsedValue,
        reason: reason.trim(),
      });
      toast.success(
        summary?.is_paid
          ? `Credit of ${cs()}${previewSavings.toFixed(2)} issued to customer`
          : `Invoice reduced by ${cs()}${previewSavings.toFixed(2)}`
      );
      setValue('');
      setReason('');
      await fetchSummary();
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply discount');
    } finally {
      setSubmitting(false);
    }
  };

  const revert = async (discountId) => {
    if (!window.confirm('Revert this discount? For credit-based discounts, the amount will be deducted from the customer balance.')) return;
    try {
      await api.post(`/admin/manual-discounts/${discountId}/revert`);
      toast.success('Discount reverted');
      await fetchSummary();
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revert discount');
    }
  };

  const discounts = summary?.discounts || [];
  const activeTotal = Number(summary?.active_discount_total || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="manual-discount-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TicketPercent className="h-5 w-5 text-emerald-600" />
            Manual Discount
          </DialogTitle>
          <DialogDescription>
            Apply a one-off discount to this transaction. Unpaid transactions are reduced on the invoice; paid transactions issue an account credit.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : summary ? (
          <div className="space-y-4">
            {/* Transaction summary */}
            <div className="rounded-lg border bg-slate-50 p-3 text-sm space-y-1">
              <div className="font-medium text-slate-800 truncate">{summary.description}</div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Current amount:</span>
                <span className="font-semibold">{cs()}{Number(summary.original_amount || 0).toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Status:</span>
                <Badge variant={summary.is_paid ? 'default' : 'secondary'} className={summary.is_paid ? 'bg-green-100 text-green-700' : ''}>
                  {summary.is_paid ? 'Paid · credit will be issued' : 'Unpaid · invoice will be reduced'}
                </Badge>
              </div>
              {activeTotal > 0 && (
                <div className="flex items-center justify-between text-emerald-700">
                  <span>Active discount total:</span>
                  <span className="font-semibold">-{cs()}{activeTotal.toFixed(2)}</span>
                </div>
              )}
            </div>

            {/* History */}
            {discounts.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-slate-500 uppercase tracking-wide">Discount history</Label>
                <div className="max-h-40 overflow-y-auto border rounded-md divide-y">
                  {discounts.map((d) => (
                    <div key={d.id} className="px-3 py-2 text-xs flex items-start justify-between gap-2" data-testid={`discount-row-${d.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-slate-800">
                            -{cs()}{Number(d.amount_saved || 0).toFixed(2)}
                          </span>
                          <span className="text-slate-500">
                            ({d.discount_type === 'percentage' ? `${d.discount_value}%` : `${cs()}${Number(d.discount_value).toFixed(2)} fixed`})
                          </span>
                          {d.status === 'active' ? (
                            <Badge className="bg-emerald-100 text-emerald-700 text-[10px] py-0 px-1.5">Active</Badge>
                          ) : (
                            <Badge className="bg-slate-200 text-slate-600 text-[10px] py-0 px-1.5">Reverted</Badge>
                          )}
                          {d.effect === 'credit_issued' && (
                            <Badge variant="outline" className="text-[10px] py-0 px-1.5">Credit</Badge>
                          )}
                        </div>
                        <div className="text-slate-600 truncate">{d.reason}</div>
                        <div className="text-slate-400 mt-0.5">
                          by {d.created_by_name} · {new Date(d.created_at).toLocaleString()}
                        </div>
                      </div>
                      {d.status === 'active' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-red-600 hover:text-red-700 shrink-0"
                          onClick={() => revert(d.id)}
                          data-testid={`revert-discount-${d.id}`}
                        >
                          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Revert
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Apply form */}
            <div className="space-y-3 pt-2 border-t">
              <Label className="text-xs text-slate-500 uppercase tracking-wide">Add new discount</Label>

              <div className="grid grid-cols-[120px_1fr] gap-2">
                <Select value={discountType} onValueChange={setDiscountType}>
                  <SelectTrigger data-testid="discount-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">
                      <span className="flex items-center gap-1"><Percent className="h-3.5 w-3.5" /> Percent</span>
                    </SelectItem>
                    <SelectItem value="fixed">
                      <span className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" /> Fixed</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <div className="relative">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    max={discountType === 'percentage' ? 100 : undefined}
                    placeholder={discountType === 'percentage' ? 'e.g. 10' : 'e.g. 25.00'}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className="pr-8"
                    data-testid="discount-value-input"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                    {discountType === 'percentage' ? '%' : '$'}
                  </span>
                </div>
              </div>

              <Textarea
                placeholder="Reason (required) — e.g. Sibling discount, Coach referral, Goodwill"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                data-testid="discount-reason-input"
              />

              {previewSavings > 0 && (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Savings:</span>
                    <span className="font-semibold text-emerald-700">-{cs()}{previewSavings.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">
                      {summary.is_paid ? 'Credit to be issued:' : 'New invoice amount:'}
                    </span>
                    <span className="font-semibold">
                      {summary.is_paid ? `+${cs()}${previewSavings.toFixed(2)}` : `${cs()}${newAmount.toFixed(2)}`}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="discount-cancel-btn">
            Close
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !summary || !value || !reason.trim()}
            data-testid="discount-apply-btn"
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Apply Discount
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
